/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { RefreshCw, Download } from 'lucide-react'
import { Button, Card, Col, Container, Form, Row, Table } from 'react-bootstrap'
import Select from 'react-select'
import Loader from '../../components/atoms/Loader'
import RowEmpty from '../../components/atoms/RowEmpty'
import TablePagination from '../../components/atoms/Table/TablePagination'
import InputDatePicker from '../../components/Input/DatePicker/InputDatePicker'
import api from '../../config/AxiosInstance'
import { convertDateWithTime } from '../../helpers/constants'
import '../../styles/style.css'
import { formatNumber } from '../../utils/TextFormatter'
import toast, { Toaster } from 'react-hot-toast'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const StockLedgerPage = () => {
  const [loading, setLoading] = useState(false)
  const [loadingDownload, setLoadingDownload] = useState(false)
  const [dataList, setDataList] = useState([])
  const [warehouses, setWarehouses] = useState([])

  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })

  const [filter, setFilter] = useState({
    warehouse_id: '',
    slot: '',
    palet: '',
    ticket: '',
    search: '',
    batch: '',
    start_date: '',
    end_date: ''
  })

  const [debouncedFilter, setDebouncedFilter] = useState({
    slot: '',
    palet: '',
    ticket: '',
    search: '',
    batch: ''
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilter((prev) => {
        const isChanged =
          prev.slot !== filter.slot ||
          prev.palet !== filter.palet ||
          prev.ticket !== filter.ticket ||
          prev.search !== filter.search ||
          prev.batch !== filter.batch

        if (!isChanged) return prev

        setMeta((prevMeta) =>
          prevMeta.current_page === 1
            ? prevMeta
            : { ...prevMeta, current_page: 1 }
        )

        return {
          slot: filter.slot,
          palet: filter.palet,
          ticket: filter.ticket,
          search: filter.search,
          batch: filter.batch
        }
      })
    }, 800)

    return () => clearTimeout(handler)
  }, [filter.slot, filter.palet, filter.ticket, filter.search, filter.batch])

  const fetchWarehouses = async () => {
    if (warehouses.length > 0) return
    try {
      const params = { is_parent: 1, forUser: 1 }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      const options = data.map((w) => ({
        value: w.id,
        label: w.warehouse_name
      }))
      setWarehouses(options)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    }
  }

  const fetchDataList = useCallback(
    async (signal) => {
      try {
        setLoading(true)
        const params = {
          per_page: meta.per_page,
          page: meta.current_page,
          warehouse_id: filter.warehouse_id || '',
          slot: debouncedFilter.slot || '',
          palet: debouncedFilter.palet || '',
          ticket: debouncedFilter.ticket || '',
          search: debouncedFilter.search || '',
          batch: debouncedFilter.batch || '',
          start_date: filter.start_date
            ? convertDateWithTime(filter.start_date, 'YYYY-MM-DD')
            : '',
          end_date: filter.end_date
            ? convertDateWithTime(filter.end_date, 'YYYY-MM-DD')
            : ''
        }
        const res = await api.get('/stock-ledger', { params, signal })

        if (res.status === 200) {
          const { data, metadata } = res.data
          const rows = data?.rows || data || []
          setDataList(rows)
          setMeta((prev) => ({
            ...prev,
            total_row: metadata?.total_row || data?.count || 0,
            total_page:
              metadata?.total_page ||
              Math.ceil((data?.count || 0) / prev.per_page) ||
              0
          }))
        }
      } catch (err) {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        toast.error(errMsg, {
          position: 'top-right',
          style: {
            fontSize: '14px',
            fontWeight: '600'
          }
        })
      } finally {
        setLoading(false)
      }
    },
    [
      meta.per_page,
      meta.current_page,
      filter.warehouse_id,
      debouncedFilter.slot,
      debouncedFilter.palet,
      debouncedFilter.ticket,
      debouncedFilter.search,
      debouncedFilter.batch,
      filter.start_date,
      filter.end_date
    ]
  )

  useEffect(() => {
    fetchWarehouses()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    fetchDataList(controller.signal)
    return () => controller.abort()
  }, [fetchDataList])

  const handleFilterChange = (name, value) => {
    setFilter((prev) => {
      const newFilter = { ...prev, [name]: value }
      if (name === 'start_date' && value && prev.end_date) {
        if (new Date(value) > new Date(prev.end_date)) {
          newFilter.end_date = value
        }
      } else if (name === 'end_date' && value && prev.start_date) {
        if (new Date(value) < new Date(prev.start_date)) {
          newFilter.start_date = value
        }
      }
      return newFilter
    })
    if (['warehouse_id', 'start_date', 'end_date'].includes(name)) {
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }
  }

  const handleReset = () => {
    setFilter({
      warehouse_id: '',
      slot: '',
      palet: '',
      ticket: '',
      search: '',
      batch: '',
      start_date: '',
      end_date: ''
    })
    setDebouncedFilter({
      slot: '',
      palet: '',
      ticket: '',
      search: '',
      batch: ''
    })
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({ ...prevMeta, current_page: data.selected + 1 }))
    window.scrollTo(0, 0)
  }

  const handleDownloadExport = async () => {
    try {
      setLoadingDownload(true)
      const params = {
        warehouse_id: filter.warehouse_id || '',
        slot: debouncedFilter.slot || '',
        palet: debouncedFilter.palet || '',
        ticket: debouncedFilter.ticket || '',
        search: debouncedFilter.search || '',
        batch: debouncedFilter.batch || '',
        start_date: filter.start_date
          ? convertDateWithTime(filter.start_date, 'YYYY-MM-DD')
          : '',
        end_date: filter.end_date
          ? convertDateWithTime(filter.end_date, 'YYYY-MM-DD')
          : ''
      }
      const res = await api.get('/stock-ledger/export', {
        params,
        responseType: 'arraybuffer'
      })
      const { status, data: dataResponse } = res
      if (status === 200) {
        const blob = new Blob([dataResponse], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'REPORT_STOCK_LEDGER.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      const err = error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(err, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setLoadingDownload(false)
    }
  }

  const warehouseOptions = useMemo(
    () => [{ value: '', label: 'All Warehouses' }, ...warehouses],
    [warehouses]
  )

  const handleNavigate = (item) => {
    if (!item.ticket_id || !item.ticket_type) return
    const { ticket_id, ticket_type } = item

    let path = ''

    switch (ticket_type) {
      case 'inbound':
        path = `/inbound/purchase-receipt/${ticket_id}`
        break
      case 'sj':
        path = `/outbound/sj/detail/${encodeURIComponent(item.ticket)}`
        break
      case 'wo':
        path = `/workorder/plan/detail/${ticket_id}`
        break
      case 'stock_adjustment':
        path = `/stock-adjustment/detail/${ticket_id}`
        break
      case 'job_manufacture':
        path = `/workorder/job-status/detail/${ticket_id}`
        break
      case 'transfer':
        path = `/transfer/transfer-area/detail/${ticket_id}`
        break
      case 'transfer_rack':
        path = `/transfer/transfer_antar_rak/detail/${ticket_id}`
        break
      default:
        console.warn('Unhandled ticket_type:', ticket_type)
        return
    }

    if (path) {
      window.open(path, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Container fluid className="px-4">
      <Toaster />
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Stock Ledger</h3>
        </Col>
        <Col className="text-end">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={handleReset}
            style={{ fontSize: '0.85rem', height: '37px' }}
          >
            <RefreshCw size={16} className="me-2" />
            Reset Filter
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Warehouse Name</Form.Label>
              <Select
                name="warehouse_id"
                id="warehouse_id"
                onChange={(e) =>
                  handleFilterChange('warehouse_id', e?.value || '')
                }
                value={warehouseOptions.find(
                  (w) => w.value === filter.warehouse_id
                )}
                options={warehouseOptions}
                styles={SelectStyles}
                placeholder="All Warehouses"
                isClearable
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Slot</Form.Label>
              <Form.Control
                type="text"
                placeholder="Input"
                value={filter.slot}
                onChange={(e) => handleFilterChange('slot', e.target.value)}
                style={FormControlStyle}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Pallet ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="Input"
                value={filter.palet}
                onChange={(e) => handleFilterChange('palet', e.target.value)}
                style={FormControlStyle}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Ticket</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter ticket"
                value={filter.ticket}
                onChange={(e) => handleFilterChange('ticket', e.target.value)}
                style={FormControlStyle}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Item</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search code or name"
                value={filter.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                style={FormControlStyle}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Batch</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search batch"
                value={filter.batch}
                onChange={(e) => handleFilterChange('batch', e.target.value)}
                style={FormControlStyle}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label style={labelStyle}>Time</Form.Label>
              <div className="d-flex align-items-center">
                <div
                  style={{ ...FormControlStyle, flex: 1 }}
                  className="d-flex align-items-center px-2"
                >
                  <InputDatePicker
                    value={filter.start_date}
                    onChange={(date) => handleFilterChange('start_date', date)}
                    maxDate={filter.end_date ? new Date(filter.end_date) : null}
                    clearIcon
                    placeholder="Select"
                  />
                </div>
                <span className="mx-2 fw-bold text-muted">-</span>
                <div
                  style={{ ...FormControlStyle, flex: 1 }}
                  className="d-flex align-items-center px-2"
                >
                  <InputDatePicker
                    value={filter.end_date}
                    onChange={(date) => handleFilterChange('end_date', date)}
                    minDate={
                      filter.start_date ? new Date(filter.start_date) : null
                    }
                    clearIcon
                    placeholder="Select"
                  />
                </div>
              </div>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row className="mb-3">
        <Col className="text-end">
          <Button
            variant="outline-warning"
            className="rounded px-4 fw-bold"
            onClick={handleDownloadExport}
            disabled={loadingDownload}
            style={{
              fontSize: '0.85rem',
              color: '#f89d1d',
              borderColor: '#f89d1d',
              backgroundColor: '#fff'
            }}
          >
            {loadingDownload ? (
              <span>
                <RefreshCw size={16} className="me-2 fa-spin" />
                Export...
              </span>
            ) : (
              <span>
                <Download size={16} className="me-2" />
                Export
              </span>
            )}
          </Button>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Table
            hover
            responsive
            className="shadow-sm mb-5 bg-white"
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              fontSize: '0.85rem'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="p-3 align-middle" style={tableHeaderStyle}>
                  Time
                </th>
                <th className="p-3 align-middle" style={tableHeaderStyle}>
                  Slot
                </th>
                <th className="p-3 align-middle" style={tableHeaderStyle}>
                  Palet
                </th>
                <th className="p-3 align-middle" style={tableHeaderStyle}>
                  Item Code
                </th>
                <th
                  className="p-3 align-middle"
                  style={{ ...tableHeaderStyle, width: '20%' }}
                >
                  Item Name
                </th>
                <th className="p-3 align-middle" style={tableHeaderStyle}>
                  Batch
                </th>
                <th
                  className="p-3 align-middle text-end"
                  style={tableHeaderStyle}
                >
                  Out Qty
                </th>
                <th
                  className="p-3 align-middle text-end"
                  style={tableHeaderStyle}
                >
                  In Qty
                </th>
                <th
                  className="p-3 align-middle text-end"
                  style={tableHeaderStyle}
                >
                  Balance
                </th>
                <th
                  className="p-3 align-middle text-center"
                  style={tableHeaderStyle}
                >
                  UOM
                </th>
                <th className="p-3 align-middle" style={tableHeaderStyle}>
                  Ticket
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="p-5">
                    <Loader />
                  </td>
                </tr>
              ) : dataList.length === 0 ? (
                <RowEmpty colSpan={11} />
              ) : (
                dataList.map((item, index) => {
                  const outQtyFormatted = formatNumber(item.qty_out)
                  const inQtyFormatted = formatNumber(item.qty_in)
                  const isOut = parseFloat(item.qty_out) < 0
                  const isIn = parseFloat(item.qty_in) > 0

                  return (
                    <tr
                      key={item.id || index}
                      style={{ borderBottom: '1px solid #f0f0f0' }}
                    >
                      <td className="p-3 align-middle">
                        {item.time ? convertDateWithTime(item.time) : '-'}
                      </td>
                      <td className="p-3 align-middle">{item.slot || '-'}</td>
                      <td className="p-3 align-middle">{item.palet || '-'}</td>
                      <td className="p-3 align-middle">
                        {item.item_code || '-'}
                      </td>
                      <td
                        className="p-3 align-middle text-truncate"
                        style={{ maxWidth: '250px' }}
                      >
                        {item.item_name || '-'}
                      </td>
                      <td className="p-3 align-middle text-muted">
                        {item.batch || '-'}
                      </td>
                      <td
                        className={`p-3 align-middle text-end ${isOut ? 'text-danger' : 'text-muted'}`}
                      >
                        {outQtyFormatted}
                      </td>
                      <td
                        className={`p-3 align-middle text-end ${isIn ? 'text-success' : 'text-muted'}`}
                      >
                        {inQtyFormatted}
                      </td>
                      <td className="p-3 align-middle text-end fw-bold">
                        {formatNumber(item.balance)}
                      </td>
                      <td className="p-3 align-middle text-center">
                        {item.uom || '-'}
                      </td>
                      <td className="p-3 align-middle">
                        <span
                          className={
                            item.ticket_id
                              ? 'text-primary fw-semibold cursor-pointer text-decoration-underline'
                              : ''
                          }
                          onClick={() => handleNavigate(item)}
                        >
                          {item.ticket || '-'}
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={11} className="p-3 border-top-0">
                  <TablePagination
                    meta={meta}
                    handleOnChangeMeta={handleOnChangeMeta}
                    handlePageChange={handlePageChange}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>
    </Container>
  )
}

export default StockLedgerPage
